import * as signalR from '@microsoft/signalr';
import { apiUrl, getApiAccessToken } from './apiAuthFetch';

// SignalR client auto-appends /negotiate to this URL
const HUB_URL = '/api/signals';

type SignalREventHandler = (data: unknown) => void;

// ---------------------------------------------------------------------------
// Broadcast logger — captures all incoming SignalR events for debug panel
// ---------------------------------------------------------------------------

const MAX_BROADCAST_LOG = 100;

export interface SignalRBroadcastEntry {
  id: string;
  timestamp: number;
  eventName: string;
  data: unknown;
}

const broadcastLog: SignalRBroadcastEntry[] = [];
const broadcastListeners = new Set<(entry: SignalRBroadcastEntry) => void>();

function logBroadcast(eventName: string, data: unknown): void {
  const entry: SignalRBroadcastEntry = {
    id: crypto.randomUUID(),
    timestamp: Date.now(),
    eventName,
    data,
  };
  broadcastLog.unshift(entry);
  if (broadcastLog.length > MAX_BROADCAST_LOG) broadcastLog.pop();
  broadcastListeners.forEach(fn => { try { fn(entry); } catch { /* ignore */ } });
}

export function getSignalRBroadcastLog(): SignalRBroadcastEntry[] {
  return [...broadcastLog];
}

export function clearSignalRBroadcastLog(): void {
  broadcastLog.length = 0;
}

export function onSignalRBroadcast(listener: (entry: SignalRBroadcastEntry) => void): () => void {
  broadcastListeners.add(listener);
  return () => { broadcastListeners.delete(listener); };
}

export type SignalRConnectionStatus = 'Connected' | 'Reconnecting' | 'Disconnected';

type ConnectionStatusListener = (status: SignalRConnectionStatus) => void;

let connection: signalR.HubConnection | null = null;
let connectionPromise: Promise<void> | null = null;

// Subscriptions that arrived before the connection was ready
const pendingSubscriptions: Array<{ eventName: string; handler: SignalREventHandler }> = [];

const statusListeners = new Set<ConnectionStatusListener>();

function notifyStatusChange(status: SignalRConnectionStatus): void {
  statusListeners.forEach(listener => listener(status));
}

/**
 * Subscribe to connection status changes. Returns an unsubscribe function.
 */
export function onConnectionStatusChange(listener: ConnectionStatusListener): () => void {
  statusListeners.add(listener);
  return () => {
    statusListeners.delete(listener);
  };
}

/**
 * Build and start the SignalR connection.
 * Safe to call multiple times — returns the existing connection if already started.
 */
export async function startSignalRConnection(): Promise<signalR.HubConnection> {
  if (connection && connection.state === signalR.HubConnectionState.Connected) {
    return connection;
  }

  if (connectionPromise) {
    await connectionPromise;
    return connection!;
  }

  connection = new signalR.HubConnectionBuilder()
    // In production negotiate is cross-origin and behind Easy Auth, so it needs the
    // API's address and a bearer token. withCredentials must be false: the client
    // defaults it to true (cookies), which a browser refuses against sm4rt-api's
    // `Access-Control-Allow-Origin: *`. The Azure SignalR connection that follows
    // uses the token negotiate returns, not this one.
    .withUrl(apiUrl(HUB_URL), {
      accessTokenFactory: async () => (await getApiAccessToken()) ?? '',
      withCredentials: false,
    })
    .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
    .configureLogging(signalR.LogLevel.Information)
    .build();

  // Apply any subscriptions that were registered before connection was created
  for (const { eventName, handler } of pendingSubscriptions) {
    connection.on(eventName, handler);
  }
  pendingSubscriptions.length = 0;

  connection.onreconnecting((error) => {
    console.warn('[SignalR] Reconnecting...', error);
    notifyStatusChange('Reconnecting');
  });

  connection.onreconnected((connectionId) => {
    console.info('[SignalR] Reconnected:', connectionId);
    notifyStatusChange('Connected');
  });

  connection.onclose((error) => {
    console.warn('[SignalR] Connection closed', error);
    connectionPromise = null;
    notifyStatusChange('Disconnected');
  });

  connectionPromise = connection.start();
  await connectionPromise;
  console.info('[SignalR] Connected');
  notifyStatusChange('Connected');
  return connection;
}

/**
 * Subscribe to a SignalR event. Returns an unsubscribe function.
 * Safe to call before the connection is established — subscriptions are queued.
 */
export function onSignalREvent(eventName: string, handler: SignalREventHandler): () => void {
  const wrappedHandler: SignalREventHandler = (data) => {
    logBroadcast(eventName, data);
    handler(data);
  };

  if (connection) {
    connection.on(eventName, wrappedHandler);
  } else {
    pendingSubscriptions.push({ eventName, handler: wrappedHandler });
  }

  return () => {
    connection?.off(eventName, wrappedHandler);
    const idx = pendingSubscriptions.findIndex(s => s.eventName === eventName && s.handler === wrappedHandler);
    if (idx >= 0) pendingSubscriptions.splice(idx, 1);
  };
}

/**
 * Stop the SignalR connection.
 */
export async function stopSignalRConnection(): Promise<void> {
  if (connection) {
    await connection.stop();
    connection = null;
    connectionPromise = null;
  }
}

/**
 * Get current connection state (for debug panel).
 */
export function getSignalRState(): string {
  if (!connection) return 'Disconnected';
  return signalR.HubConnectionState[connection.state];
}
